/** /prep-lists/$id: the editor. A day-by-day list and a weekly plan are two
 *  screens (docs/parity/prep-lists.md §6); `is_weekly` never changes after
 *  a list is made, so the detail picks one. Both read the same cached detail
 *  this route loads. */
import { createFileRoute } from '@tanstack/react-router';
import { usePrepList } from '@/api/queries/prep-lists';
import { ComingSoon, useFeatureGate } from '@/components/ComingSoon';
import { DayEditor } from '@/features/prep-lists/DayEditor';
import { isNotFound, ListLoadFailed, ListLoading, ListNotFound } from '@/features/prep-lists/EditorStates';
import { WeeklyEditor } from '@/features/prep-lists/WeeklyEditor';

export const Route = createFileRoute('/_app/prep-lists/$id')({
  component: PrepListEditorRoute,
});

function PrepListEditorRoute() {
  if (useFeatureGate('prep')) return <ComingSoon feature="prep" />;
  return <Editor />;
}

function Editor() {
  const { id } = Route.useParams();
  const prepListId = Number(id);
  const valid = Number.isInteger(prepListId) && prepListId > 0;
  const { data, isError, error, refetch, isFetching } = usePrepList(valid ? prepListId : undefined);

  if (!valid) return <ListNotFound />;
  // Only before the first load: a background refetch that fails later (a
  // live update, the wifi dropping) must never swap an open editor, and its
  // unsaved changes, for an error page. The next Save finds out instead.
  if (!data) {
    if (isError) return isNotFound(error) ? <ListNotFound /> : <ListLoadFailed onRetry={() => void refetch()} retrying={isFetching} />;
    return <ListLoading />;
  }
  // Keyed by id: moving from one list to another starts a fresh session.
  return data.is_weekly ? <WeeklyEditor key={prepListId} prepListId={prepListId} /> : <DayEditor key={prepListId} prepListId={prepListId} />;
}
